import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { MapMovedEvent, Map, Coordinate } from '@sembo-private/maps';
import type { MapComponent } from '@sembo-private/vue3-maps';

import { MapStateKey } from '@apps/main/modules/state/state';
import { injectStrict } from '@apps/main/modules/helpers/injectStrict';
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import { mapLanguageLocale } from '@apps/main/modules/localization/localeUtils';

import { useConfigurationPanelStore } from '../configurationPanel/configurationPanelStore';

import {
  getLocationAndDestinationMarkers,
  getMarkerFormatters,
} from './mapStoreLogic';

export type MapProperties = InstanceType<typeof MapComponent>['$props'];

export const useMapStore = defineStore('MapStore', () => {
  const mapState = injectStrict(MapStateKey);
  const statsigEventsStore = useStatsigEventsStore();
  const configurationPanelStore = useConfigurationPanelStore();

  // State
  const isMapCreated = ref<boolean>(false);
  const isFirstMapRendered = ref<boolean>(true);
  const shouldShowFullScreenMap = ref<boolean>(false);

  // Actions
  function mapViewChanged(event: MapMovedEvent): void {
    mapState.setMapMovedAndCreateNewPolygon(event);
  }

  function isMarkerInStartRectangleMapView(
    markerCoordinates: Coordinate
  ): boolean {
    const startRectangle = mapState.DEFAULT_RECTANGLE;

    return (
      markerCoordinates.latitude < startRectangle.topLeft.latitude &&
      markerCoordinates.latitude > startRectangle.bottomRight.latitude &&
      markerCoordinates.longitude > startRectangle.topLeft.longitude &&
      markerCoordinates.longitude < startRectangle.bottomRight.longitude
    );
  }

  function setDynamicMapIntegration(integration: Map): void {
    isFirstMapRendered.value = false;
    isMapCreated.value = true;
    mapState.setMapIntegration(integration);
  }

  function openFullScreenDynamicTripMap(): void {
    statsigEventsStore.logTripOverviewOpenFullScreenMap();
    shouldShowFullScreenMap.value = true;
  }

  function closeFullScreenDynamicTripMap(): void {
    shouldShowFullScreenMap.value = false;
    mapState.mapShouldCenterOnMarkers.value = true;
  }

  // Getters
  const mapProperties = computed<MapProperties>(() => {
    const markersWithUserLocation = mapState.startingLocationMarker.value
      ? [mapState.startingLocationMarker.value, ...mapState.markers.value]
      : mapState.markers.value;
    const markers = getLocationAndDestinationMarkers(markersWithUserLocation);
    const startRectangle =
      markers.length === 0 ? mapState.DEFAULT_RECTANGLE : undefined;

    return {
      markers,
      markerFormatters: getMarkerFormatters(),
      route: mapState.mapRoute.value,
      showAllMarkers: mapState.mapShouldCenterOnMarkers.value,
      startRectangle,
      gestureHandling: 'greedy',
      locale: mapLanguageLocale,
    };
  });

  const isMapLoading = computed(
    () =>
      !isMapCreated.value ||
      (mapState.calculatedRoute.value.length === 0 &&
        (configurationPanelStore.editTripCarDetails?.segments.length ?? 0) > 2)
  );

  return {
    // State
    isFirstMapRendered,
    isMapCreated,
    shouldShowFullScreenMap,
    isMapLoading,
    // Actions
    mapViewChanged,
    isMarkerInStartRectangleMapView,
    setDynamicMapIntegration,
    openFullScreenDynamicTripMap,
    closeFullScreenDynamicTripMap,

    // Getters
    mapProperties,
  };
});
